import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import FoldersService from '../../../services/FoldersService';
import AppointmentsService from '../../../services/AppointmentsService';
import { Add, Edit } from '@mui/icons-material';

import InfoBox from '../../blocks/messages/info-box';
import CommonButton from '../../buttons/common-button';
import AddNewPaymentModal from './modals/modal--folder--add-new-payment';
import EditPaymentModal from './modals/modal--folder--edit-payment';
import CustomModal from '../../blocks/custom-modal';

/**
 * PaymentsOverview component to display and manage all payments related to a folder.
 * Business rules:
 * - "Total réglé" = (Sum of all entered payments + down payment) - QualiRepar bonus (if any).
 * - Allow editing QualiRepar bonus amount.
 * - Allow editing the amount of each individual payment.
 * - Single consolidated table for clarity (includes payments rows, down payment row, QualiRepar row, infos row, and totals).
 * Text displayed to users remains in French.
 */
const PaymentsOverview = ({ showEditButton = false }) => {
  const { refFolder } = useParams();

  /** Data states */
  const [folder, setFolder] = useState(null);
  const [appointmentsData, setAppointmentsData] = useState([]);
  const [bonusQualiRepar, setBonusQualiRepar] = useState(null);
  const [paymentsData, setPaymentsData] = useState([]);

  /** UI states */
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  /** Edit QualiRepar states */
  const [editQualiOpen, setEditQualiOpen] = useState(false);
  const [newQualiAmount, setNewQualiAmount] = useState('');

  /** Edit Payment modal (external component) */
  const [editPaymentOpen, setEditPaymentOpen] = useState(false);
  const [paymentBeingEdited, setPaymentBeingEdited] = useState(null);

  /** Labels for payment modes (display only) */
  const paymentModeLabels = {
    cheque: 'Chèque',
    espece: 'Espèce',
    cb: 'Carte bancaire',
    virement: 'Virement bancaire',
    paiement_a_distance: 'Paiement à distance',
  };

  /** Format number to euro currency string with 2 decimals (fr-FR). */
  const formatCurrency = (value) => {
    const n = Number.isFinite(value) ? value : 0;
    return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  };

  /** Format an ISO/date string to fr-FR datetime. Fallback to "-" if falsy. */
  const formatDateTime = (dateLike) => {
    if (!dateLike) return '-';
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('fr-FR');
  };

  /** Fetch folder + appointments + derived bonus + payments. */
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const folderResult = await FoldersService.getFolderByNumber(refFolder);
      if (!folderResult) {
        setError('Dossier introuvable');
        return;
      }
      setFolder(folderResult);

      const appts = await AppointmentsService.getAppointmentsByFolderId(folderResult.id);
      setAppointmentsData(appts || []);

      if (appts?.[0] && appts[0].is_quali_repar && !folderResult.quote_payment) {
        setBonusQualiRepar(Number(appts[0].quali_repar_amount) || 0);
      } else {
        setBonusQualiRepar(null);
      }

      setPaymentsData(Array.isArray(folderResult.payments) ? folderResult.payments : []);
    } catch (err) {
      setError(err?.message || 'Une erreur inattendue est survenue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** QualiRepar editing handlers */
  const handleQualiEdit = () => {
    setNewQualiAmount(
      typeof bonusQualiRepar === 'number' && !Number.isNaN(bonusQualiRepar) ? String(bonusQualiRepar) : ''
    );
    setEditQualiOpen(true);
  };

  const saveQualiAmount = async () => {
    try {
      const firstPresent = appointmentsData.find((a) => a.is_client_present);
      if (!firstPresent) {
        console.error('No appointment where the client was present.');
        return;
      }

      const parsed = parseFloat(newQualiAmount);
      if (Number.isNaN(parsed) || parsed < 0) {
        console.error('Invalid QualiRepar amount.');
        return;
      }

      await AppointmentsService.editAppointment(firstPresent.id, {
        appointment_date: firstPresent.appointment_date,
        comments: firstPresent.comments,
        is_client_present: firstPresent.is_client_present,
        ids_appointment_action: firstPresent.appointment_actions.map((action) => action.id),
        is_quali_repar: true,
        quali_repar_amount: parsed,
      });

      setEditQualiOpen(false);
      await fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  /** Open edit-payment modal */
  const handleOpenEditPayment = (payment) => {
    setPaymentBeingEdited(payment);
    setEditPaymentOpen(true);
  };

  /** Derived totals (memoized) */
  const totals = useMemo(() => {
    const sumPayments = paymentsData.reduce((sum, p) => sum + (Number(p?.amount) || 0), 0);
    const downPayment = Number(folder?.down_payment) || 0;
    const quali = Number(bonusQualiRepar) || 0;
    const quoteAmount = Number(folder?.quote_amount) || 0;

    /** Total réglé = (Sum payments + down payment) - QualiRepar bonus */
    const totalPaid = sumPayments + downPayment - quali;

    return {
      sumPayments,
      downPayment,
      quali,
      totalPaid,
      quoteAmount,
    };
  }, [paymentsData, folder?.down_payment, folder?.quote_amount, bonusQualiRepar]);

  if (loading) {
    return (
      <div className="folder-section payments-container w-100">
        <h2>Règlements</h2>
        Chargement des données...
      </div>
    );
  }

  if (error || !folder) {
    return (
      <div className="folder-section payments-container w-100">
        <h2>Règlements</h2>
        <InfoBox type="error" disabledCloseBtn={true} text={error || 'Dossier introuvable'} />
      </div>
    );
  }

  return (
    <>
      <div className="folder-section payments-container w-100">
        <h2>Règlements</h2>

        {/* Wrapper pour scroll horizontal sur mobile */}
        <div className="payments-table-wrapper">
          {/* Single consolidated table */}
          <table className="payments-table w-100">
            <thead>
              <tr>
                <th>Date</th>
                <th>Mode de paiement</th>
                <th className="text-right">Montant</th>
                {showEditButton && <th style={{ width: 1 }}></th>}
              </tr>
            </thead>

            <tbody>
              {/* Entered payments (editable amount) */}
              {paymentsData.length === 0 && (
                <tr>
                  <td colSpan={showEditButton ? 4 : 3} className="text-center">
                    Aucun règlement saisi.
                  </td>
                </tr>
              )}

              {paymentsData.map((payment) => (
                <tr key={payment.id ?? `${payment.payment_mode}-${payment.payment_date}`}>
                  <td>{formatDateTime(payment.payment_date)}</td>
                  <td>{paymentModeLabels[payment.payment_mode] || payment.payment_mode || '-'}</td>
                  <td className="text-right">{formatCurrency(Number(payment.amount) || 0)}</td>
                  {showEditButton && (
                    <td>
                      <button
                        className="edit-bonus-button"
                        onClick={() => handleOpenEditPayment(payment)}
                        title="Modifier le montant du règlement"
                      >
                        Éditer
                        <Edit />
                      </button>
                    </td>
                  )}
                </tr>
              ))}

              {/* Down payment (Acompte) */}
              <tr className="downpayment-row">
                <td colSpan={2}>
                  <strong>Acompte</strong>
                </td>
                <td className="text-right">
                  <strong>{formatCurrency(totals.downPayment)}</strong>
                </td>
                {showEditButton && <td></td>}
              </tr>

              {/* QualiRepar bonus (displayed as negative line) */}
              <tr className="bonus-row">
                <td colSpan={2}>
                  <p className="m-0">
                    <strong>Bonus QUALIREPAR</strong> <i>(Déduit du total réglé)</i>
                  </p>
                </td>
                <td className="text-right">
                  <span className="bonus-amount">− {formatCurrency(totals.quali)}</span>
                </td>
                {showEditButton && (
                  <td>
                    <button
                      className="edit-bonus-button"
                      onClick={handleQualiEdit}
                      title="Modifier le montant QualiRepar"
                    >
                      Éditer
                      <Edit />
                    </button>
                  </td>
                )}
              </tr>

              {/* Informations complémentaires (full width text row inside the same table) */}
              <tr className="additional-infos-row">
                <td colSpan={showEditButton ? 4 : 3}>
                  <strong>Informations complémentaires : </strong>
                  {folder?.additional_informations?.trim() ? folder.additional_informations : '—'}
                </td>
              </tr>
            </tbody>

            <tfoot>
              <tr>
                <td colSpan={2}>
                  <strong>Total réglé</strong>
                </td>
                <td className="text-right">
                  <strong>{formatCurrency(totals.totalPaid)}</strong>
                </td>
                {showEditButton && <td></td>}
              </tr>
              <tr>
                <td colSpan={2}>
                  <strong>Montant du devis</strong>
                </td>
                <td className="text-right">
                  <strong>{formatCurrency(totals.quoteAmount)}</strong>
                </td>
                {showEditButton && <td></td>}
              </tr>
            </tfoot>
          </table>
        </div>

        {showEditButton && (
          <CommonButton
            className="edit-button"
            onClick={() => setPaymentModalOpen(true)}
            label="Ajouter un règlement"
            icon={<Add />}
            square
          />
        )}
      </div>

      {/* Add payment modal */}
      <AddNewPaymentModal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        idFolder={folder.id}
        onAddPayment={fetchData}
      />

      {/* Edit payment modal (separate file) */}
      <EditPaymentModal
        open={editPaymentOpen}
        onClose={() => {
          setEditPaymentOpen(false);
          setPaymentBeingEdited(null);
        }}
        payment={paymentBeingEdited}
        onSaved={fetchData}
      />

      {/* Edit QualiRepar Modal */}
      <CustomModal
        open={editQualiOpen}
        onClose={() => setEditQualiOpen(false)}
        title="Modifier le montant QualiRepar"
        handleSave={saveQualiAmount}
        handleSaveLabel="Enregistrer"
      >
        <div className="edit-quali-modal-content">
          <label>
            Montant QualiRepar :
            <input
              type="number"
              value={newQualiAmount}
              onChange={(e) => setNewQualiAmount(e.target.value)}
              placeholder="Montant en €"
              min="0"
              step="0.01"
            />
          </label>
          <p className="note">
            <strong>Note :</strong> Le bonus QualiRepar est soustrait du total réglé.
          </p>
        </div>
      </CustomModal>
    </>
  );
};

PaymentsOverview.propTypes = {
  showEditButton: PropTypes.bool,
};

export default PaymentsOverview;
